import {SubmissionDto, SubmissionFilterDto} from "../../client/submission/SubmissionApiClient";
import {useCallback, useMemo, useState} from "react";
import {GridColDef} from "@mui/x-data-grid-pro";
import {useSnackbar} from "@dain/mui-components";
import {SubmissionTranslKey} from "../submission/SubmissionTranslKey";
import {DateTimeFormatter} from "../../common/DateTimeFormatter";
import {formatUserLastNameFirst} from "../user/UserFormatter";
import {useTranslation} from "react-i18next";
import {DigitalActionAuthorizationResult} from "../../client/submission/SubmissionAuthorizationApiClient";
import {useClientLocator} from "../../client/ApiClientLocator";
import Button from "../../common/button/Button";
import {useCatalog} from "../../common/catalog/UseCatalog";
import {CatalogType} from "../../client/catalog/CatalogType";
import {TFunction} from "i18next";
import {FilterSearchType} from "../person/PersonSearchPanel";
import PageableGrid, {PageLoadSize} from "../../common/pagination/PageableGrid";
import {isSubmissionNotReadyForAuthorizationError} from "../submission/authorization/AuthorizationRetryPolicy";

function buildSubmissionGridColumns(reAuthSubmissionId: number | null, retryAuthorization: (submissionId: number) => void,
                                    t: TFunction) {

    const submissionGridColumns: GridColDef[] = [
        {
            field: "#documentMetadata.type",
            headerName: t(SubmissionTranslKey.DOCUMENT_TYPE),
            width: 230,
            valueGetter: params => params.row.documentMetadata.type.code,
            valueFormatter: params => t(params.value, {ns: CatalogType.DOCUMENT_TYPE})
        },
        {
            field: "submittedAt",
            type: "dateTime",
            headerName: t(SubmissionTranslKey.SUBMITTED_AT),
            width: 200,
            valueGetter: params => params.value ? new Date(params.value) : undefined,
            valueFormatter: params => DateTimeFormatter.formatDateTime(params.value)
        },
        {
            field: "submittedBy",
            headerName: t(SubmissionTranslKey.SUBMITTED_BY),
            width: 200,
            valueFormatter: params => formatUserLastNameFirst(params.value)
        },
        {
            field: "action",
            headerName: t(SubmissionTranslKey.SUBMISSIONS_TO_AUTHORIZE_ACTION_NAME),
            width: 200,
            renderCell: (params) => {
                const submissionId = params.row.id;

                if (!submissionId) {
                    return null;
                }

                return (
                    <Button
                        variant="primary"
                        size={"s"}
                        disabled={reAuthSubmissionId !== null && reAuthSubmissionId !== submissionId}
                        inProgress={reAuthSubmissionId === submissionId}
                        onClick={() => retryAuthorization(submissionId)}
                    >
                        {t(SubmissionTranslKey.SUBMISSIONS_TO_AUTHORIZE_ACTION)}
                    </Button>
                );
            },
        },
    ];

    return submissionGridColumns;
}

export interface SubmissionsToAuthorizeGridProps {
    rowsData: SubmissionDto[];
    totalCount: number;

    nextPageToken?: string | null;
    submissionFilter: SubmissionFilterDto;
    fetchSubmissions: (filter: SubmissionFilterDto, searchType: FilterSearchType) => void;
    reloadSubmissions: () => void;
}

export default function SubmissionsToAuthorizeGrid(props: Readonly<SubmissionsToAuthorizeGridProps>) {
    const {rowsData, totalCount, nextPageToken, submissionFilter, fetchSubmissions, reloadSubmissions} = props;

    const {t} = useTranslation();
    const snackbar = useSnackbar();
    const {submissionAuthorizationClient} = useClientLocator();
    const {isLoading: documentTypeCatalogLoading} = useCatalog(CatalogType.DOCUMENT_TYPE);
    const [reAuthSubmissionId, setReAuthSubmissionId] = useState<number | null>(null);

    const retryAuthorization = useCallback(
        (submissionId: number) => {
            setReAuthSubmissionId(submissionId);
            submissionAuthorizationClient.reinitDigitalActionAuthorization(submissionId + "")
                .then((result) => {
                    if (result && result.result === DigitalActionAuthorizationResult.SUCCESS && result.postInitRedirectUri) {
                        window.location.href = result.postInitRedirectUri;
                    } else {
                        snackbar.error(t(SubmissionTranslKey.AUTHORIZATION_REINITALIZATION_FAILED), "Reinitialization failed");
                    }
                })
                .catch((err) => {
                    if (isSubmissionNotReadyForAuthorizationError(err)) {
                        snackbar.warning(t(SubmissionTranslKey.SUBMISSION_NOT_READY_FOR_AUTHORIZATION));
                        reloadSubmissions();
                    } else {
                        snackbar.error(t(SubmissionTranslKey.AUTHORIZATION_REINITALIZATION_FAILED), err);
                    }
                })
                .finally(() => {
                    setReAuthSubmissionId(null);
                });
        },
        [setReAuthSubmissionId, submissionAuthorizationClient, snackbar, t, reloadSubmissions]
    );

    const columns: GridColDef<SubmissionDto>[] = useMemo(() => buildSubmissionGridColumns(reAuthSubmissionId, retryAuthorization, t),
        [t, retryAuthorization, reAuthSubmissionId]
    );

    const handleFetchMore = useCallback(() => {
        if (!nextPageToken) {
            return;
        }

        const nextPageFilter: SubmissionFilterDto = {
            ...submissionFilter,
            paging: {
                maxPageSize: PageLoadSize.NEXT_PAGE_SIZE,
                pageToken: nextPageToken,
            },
        };

        fetchSubmissions(nextPageFilter, FilterSearchType.NEXT_PAGE_SEARCH);
    }, [nextPageToken, submissionFilter, fetchSubmissions]);

    return (
        <>
            {!documentTypeCatalogLoading &&
                <PageableGrid
                    rowsData={rowsData}
                    totalRowsCount={totalCount}
                    columns={columns}
                    catalogLoading={documentTypeCatalogLoading}
                    onFetchMore={handleFetchMore}
                />
            }
        </>
    );
}
